/**
 * SignupWizard — orchestrator for the 4-step post-OAuth signup flow.
 * (Was 5 steps; the 'persona' mode picker was removed 2026-07-28 — lawyers-only
 * product, mode is always 'lawyer'.)
 *
 * Renders one step at a time, tracks progress server-side via /signup/state so
 * a closed-tab user resumes at the right step. Calls /signup/finalize after the
 * last step (intent_and_source) succeeds; the parent App.tsx gate then
 * re-renders with is_onboarded=true.
 *
 * Each step component fires telemetry through useSignupTelemetry — there
 * is no way to advance without an event being recorded.
 */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setUser } from '../../store/authSlice';
import type { RootState } from '../../store';
import { signupApi, StepName, STEP_ORDER, PracticeAreaStepPayload } from './signupApi';
import { useSignupState } from './useSignupState';
import { useSignupTelemetry } from './useSignupTelemetry';
import SignupStepShell from './SignupStepShell';
import PracticeAreaStep from './PracticeAreaStep';
import ProfessionalIdentityStep from './steps/ProfessionalIdentityStep';
import FirmShapeStep from './steps/FirmShapeStep';
import IntentAndSourceStep from './steps/IntentAndSourceStep';
import type { PracticeArea } from '../../types/practiceArea';

interface SignupWizardProps {
  /** Called after /signup/finalize confirms users.is_onboarded=true. */
  onComplete: () => void;
}

const STEP_TITLES: Record<StepName, { title: string; subtitle?: string }> = {
  practice_area: {
    title: 'Welcome to iAdvocate',
    subtitle: "Let's personalise your experience. Step 1 of 4.",
  },
  professional_identity: {
    title: 'About you',
    subtitle: 'A few quick details about your practice. Step 2 of 4.',
  },
  firm_shape: {
    title: 'Your practice setup',
    subtitle: 'Where do you practise? Step 3 of 4.',
  },
  intent_and_source: {
    title: 'What brings you here',
    subtitle: 'Your goals + how you found us. Final step.',
  },
};

const SignupWizard: React.FC<SignupWizardProps> = ({ onComplete }) => {
  const state = useSignupState();
  const dispatch = useDispatch();
  const authUser = useSelector((s: RootState) => s.auth.user);
  const [finalizing, setFinalizing] = useState<'idle' | 'pending' | 'error'>('idle');

  const advance = () => {
    if (!state.currentStep) return;
    const idx = STEP_ORDER.indexOf(state.currentStep);
    state.markCompleted(state.currentStep);
    // If this was the last step, finalize and notify the parent gate.
    if (idx === STEP_ORDER.length - 1) {
      finalizeAndExit();
    }
  };

  const finalizeAndExit = async () => {
    setFinalizing('pending');
    try {
      await signupApi.finalize();
      // Refresh local user state so App.tsx's gate re-evaluates.
      if (authUser) {
        dispatch(setUser({ ...authUser, is_onboarded: true }));
      }
      onComplete();
    } catch (err) {
      // Surface a visible retry — a silent failure here traps the user in
      // the wizard forever (all steps complete server-side, gate never opens).
      console.error('[signup] finalize failed', err);
      setFinalizing('error');
    }
  };

  // current_step === null from /signup/state means every step is already
  // completed/skipped server-side — the only remaining action is finalize.
  // Without this, a user whose finalize was interrupted re-enters the wizard
  // at step 1 forever (advance() no-ops on a null step).
  useEffect(() => {
    if (!state.loading && !state.isOnboarded && state.currentStep === null && finalizing === 'idle') {
      finalizeAndExit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.loading, state.currentStep, state.isOnboarded]);

  const goBackOneStep = (current: StepName) => {
    const idx = STEP_ORDER.indexOf(current);
    if (idx <= 0) return;
    const target = STEP_ORDER[idx - 1];
    // Fire back telemetry; the API call is best-effort, navigation is local.
    signupApi.back(current, target).catch(() => {});
    state.setCurrentStep(target);
  };

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface">
        <div className="text-text-secondary text-sm">Loading…</div>
      </div>
    );
  }

  // All steps already complete — never re-render step 1 here; finalize runs
  // via the effect above and this branch shows its progress / retry state.
  if (state.currentStep === null) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-surface gap-4">
        {finalizing === 'error' ? (
          <>
            <div className="text-text-secondary text-sm">
              We couldn't finish setting up your account. Please try again.
            </div>
            <button
              type="button"
              onClick={finalizeAndExit}
              className="px-6 py-2 rounded-md bg-primary-700 text-white text-sm font-medium hover:bg-primary-800"
            >
              Try again
            </button>
          </>
        ) : (
          <div className="text-text-secondary text-sm">Completing your setup…</div>
        )}
      </div>
    );
  }

  const step = state.currentStep;
  const titles = STEP_TITLES[step];

  return (
    <SignupStepShell
      stepName={step}
      title={titles.title}
      subtitle={titles.subtitle}
      onBack={STEP_ORDER.indexOf(step) > 0 ? () => goBackOneStep(step) : undefined}
    >
      {step === 'practice_area' && (
        <PracticeAreaStepWrapper
          authUserPracticeArea={authUser?.primary_practice_area ?? undefined}
          onAdvance={advance}
        />
      )}
      {step === 'professional_identity' && (
        <ProfessionalIdentityStep onAdvance={advance} onBack={() => goBackOneStep(step)} />
      )}
      {step === 'firm_shape' && (
        <FirmShapeStep onAdvance={advance} onBack={() => goBackOneStep(step)} />
      )}
      {step === 'intent_and_source' && (
        <IntentAndSourceStep onAdvance={advance} onBack={() => goBackOneStep(step)} />
      )}
    </SignupStepShell>
  );
};

/**
 * Step 1 wrapper — the existing PracticeAreaStep is reused as-is.
 * We adapt its `onSelect` callback into a complete-step telemetry call.
 */
const PracticeAreaStepWrapper: React.FC<{
  authUserPracticeArea: PracticeArea | undefined;
  onAdvance: () => void;
}> = ({ authUserPracticeArea, onAdvance }) => {
  const { complete, pending } = useSignupTelemetry({
    stepName: 'practice_area',
    onAdvance,
  });
  const handleSelect = (practiceAreas: PracticeArea[]) => {
    const payload: PracticeAreaStepPayload = { practice_areas: practiceAreas };
    complete(payload as unknown as Record<string, unknown>);
  };
  return (
    <PracticeAreaStep
      initialPrimary={authUserPracticeArea}
      onSelect={handleSelect}
      loading={pending}
    />
  );
};

export default SignupWizard;
